import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';

export const KaeuferSuchprofil = () => {
  const [formData, setFormData] = useState({
    email: '',
    assetklassen: [],
    plz_gebiete: '',
    budget_min: '',
    budget_max: '',
    wault_min: '',
    zustand_praeferenz: ['neuwertig', 'gut'],
    marketing_consent: false
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [profileToken, setProfileToken] = useState('');
  const [loading, setLoading] = useState(false);

  const assetklassen = [
    { value: 'lebensmittelmarkt', label: 'Lebensmittelmarkt' },
    { value: 'discounter', label: 'Discounter & Drogerie' },
    { value: 'baumarkt', label: 'Baumarkt & Gartencenter' },
    { value: 'fachmarkt', label: 'Fachmarkt' },
    { value: 'fachmarktzentrum', label: 'Fachmarktzentrum' }
  ];

  const zustaende = [
    { value: 'neuwertig', label: 'Neuwertig' },
    { value: 'gut', label: 'Gut' },
    { value: 'renovierungsbedarf', label: 'Renovierungsbedarf' }
  ];

  const toggleAssetklasse = (value) => {
    if (formData.assetklassen.includes(value)) {
      setFormData({
        ...formData,
        assetklassen: formData.assetklassen.filter(a => a !== value)
      });
    } else {
      setFormData({
        ...formData,
        assetklassen: [...formData.assetklassen, value]
      });
    }
  };

  const toggleZustand = (value) => {
    if (formData.zustand_praeferenz.includes(value)) {
      setFormData({
        ...formData,
        zustand_praeferenz: formData.zustand_praeferenz.filter(z => z !== value)
      });
    } else {
      setFormData({
        ...formData,
        zustand_praeferenz: [...formData.zustand_praeferenz, value]
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || formData.assetklassen.length === 0 || !formData.plz_gebiete || 
        !formData.budget_min || !formData.budget_max || !formData.marketing_consent) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus und stimmen Sie der Marketing-Nutzung zu');
      return;
    }

    if (parseInt(formData.budget_max) < parseInt(formData.budget_min)) {
      toast.error('Budget-Maximum muss größer als Minimum sein');
      return;
    }

    setLoading(true);

    try {
      const plz_gebiete = formData.plz_gebiete.split(',').map(p => p.trim()).filter(p => p);
      
      const requestData = {
        email: formData.email,
        assetklassen: formData.assetklassen,
        plz_gebiete: plz_gebiete,
        budget_min: parseInt(formData.budget_min),
        budget_max: parseInt(formData.budget_max),
        wault_min: formData.wault_min ? parseFloat(formData.wault_min) : null,
        zustand_praeferenz: formData.zustand_praeferenz,
        marketing_consent: formData.marketing_consent
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/buyer-profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Profil-Erstellung fehlgeschlagen');
      }

      const data = await response.json();
      setProfileToken(data.id);
      setSubmitted(true);
      toast.success('Suchprofil erfolgreich erstellt!');
      
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Fehler bei der Profil-Erstellung');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <SEO title="Suchprofil erstellt" url="/kaeufer/suchprofil" />
        <Navbar />
        <main className="flex-grow bg-gray-50 py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="glass-card p-10 text-center" style={{ background: 'linear-gradient(to bottom, white 0%, #f0fdf4 100%)', border: '2px solid #10b981' }}>
              <CheckCircle2 className="h-20 w-20 mx-auto mb-6" style={{ color: '#10b981' }} />
              <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Suchprofil erfolgreich erstellt!
              </h1>
              <p className="text-lg mb-6" style={{ color: 'var(--gray-700)' }}>
                Wir haben Ihnen eine E-Mail an <strong>{formData.email}</strong> gesendet.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg mb-8 text-left">
                <h3 className="font-bold mb-3" style={{ color: 'var(--navy-800)' }}>Was passiert jetzt?</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>✓ Sie erhalten eine Email mit Ihrem persönlichen Zugangs-Link</li>
                  <li>✓ Wir prüfen täglich neue Objekte auf Übereinstimmung</li>
                  <li>✓ Bei passenden Angeboten informieren wir Sie sofort</li>
                  <li>✓ Alle 3 Monate: Erinnerung zur Profil-Aktualisierung</li>
                </ul>
              </div>
              
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-semibold mb-2">Ihr Profil-Token:</p>
                <code className="text-base font-mono px-4 py-2 bg-white rounded" style={{ color: 'var(--navy-900)' }}>
                  {profileToken}
                </code>
                <p className="text-xs mt-2" style={{ color: 'var(--gray-500)' }}>
                  Bitte speichern Sie diesen Code.
                </p>
              </div>
              
              <Button onClick={() => window.location.href = '/'} className="btn-gold-gradient text-white border-0 font-semibold px-8 py-4">
                Zur Startseite
              </Button>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Käufer-Suchprofil anlegen" url="/kaeufer/suchprofil" />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1 className="text-4xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
              Käufer-Suchprofil anlegen
            </h1>
            <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--gray-700)' }}>
              Legen Sie Ihre Suchkriterien fest und erhalten Sie Benachrichtigungen bei passenden Objekten.
            </p>
          </div>

          <Card className="glass-card p-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' }}>
                <Search className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold" style={{ color: 'var(--navy-800)' }}>Ihre Suchkriterien</h2>
            </div>

            <div className="space-y-8">
              <div>
                <Label htmlFor="email" className="text-base font-semibold mb-2" style={{ color: 'var(--navy-700)' }}>
                  E-Mail <span className="text-red-500">*</span>
                </Label>
                <Input id="email" type="email" placeholder="ihre@email.de" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="h-12" />
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">Assetklassen <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {assetklassen.map((ak) => (
                    <div key={ak.value} onClick={() => toggleAssetklasse(ak.value)} className={`p-4 rounded-lg border-2 cursor-pointer ${formData.assetklassen.includes(ak.value) ? 'border-[#D4A853] bg-[#D4A853]/10' : 'border-gray-200'}`}>
                      <div className="flex items-center gap-3">
                        <Checkbox checked={formData.assetklassen.includes(ak.value)} className="pointer-events-none" />
                        <span className="font-medium">{ak.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="plz" className="text-base font-semibold mb-2">PLZ-Gebiete <span className="text-red-500">*</span></Label>
                <Input id="plz" placeholder="z.B. 50, 51, 80-89" value={formData.plz_gebiete} onChange={(e) => setFormData({...formData, plz_gebiete: e.target.value})} className="h-12" />
                <p className="text-xs mt-1" style={{ color: 'var(--gray-500)' }}>Komma-getrennt. Ranges: 80-89</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="budget_min">Budget Min € <span className="text-red-500">*</span></Label>
                  <Input id="budget_min" type="number" placeholder="2000000" value={formData.budget_min} onChange={(e) => setFormData({...formData, budget_min: e.target.value})} className="h-12" />
                </div>
                <div>
                  <Label htmlFor="budget_max">Budget Max € <span className="text-red-500">*</span></Label>
                  <Input id="budget_max" type="number" placeholder="5000000" value={formData.budget_max} onChange={(e) => setFormData({...formData, budget_max: e.target.value})} className="h-12" />
                </div>
              </div>

              <div>
                <Label htmlFor="wault_min">WAULT Min (Jahre) <span className="text-gray-400 text-sm">(optional)</span></Label>
                <Input id="wault_min" type="number" step="0.1" placeholder="z.B. 5" value={formData.wault_min} onChange={(e) => setFormData({...formData, wault_min: e.target.value})} className="h-12" />
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">Objektzustand</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {zustaende.map((z) => (
                    <div key={z.value} onClick={() => toggleZustand(z.value)} className={`p-4 rounded-lg border-2 cursor-pointer ${formData.zustand_praeferenz.includes(z.value) ? 'border-[#D4A853] bg-[#D4A853]/10' : 'border-gray-200'}`}>
                      <Checkbox checked={formData.zustand_praeferenz.includes(z.value)} className="pointer-events-none mr-2" />
                      <span className="font-medium">{z.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-200">
                <div className="flex items-start gap-3">
                  <Checkbox id="marketing_consent" checked={formData.marketing_consent} onCheckedChange={(checked) => setFormData({...formData, marketing_consent: checked})} className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="marketing_consent" className="text-base font-semibold cursor-pointer">Marketing-Einwilligung <span className="text-red-500">*</span></Label>
                    <p className="text-sm mt-2 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
                      Ich erlaube RetailInvest, mein Suchprofil für Marketing zu verwenden (z.B. Social Media: GESUCHT: Lebensmittelmarkt Köln, € 3-5 Mio.).
                    </p>
                  </div>
                </div>
              </div>

              <Button onClick={handleSubmit} disabled={loading} className="w-full btn-gold-gradient text-white border-0 font-semibold py-6 text-lg">
                {loading ? 'Erstelle...' : 'Suchprofil kostenlos erstellen'}
                {!loading && <CheckCircle2 className="ml-2 h-5 w-5" />}
              </Button>
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default KaeuferSuchprofil;
